/**
 * What the armed tool will create — the right panel *before* the first click.
 *
 * The inspector answers "what is this?" about something already on the plan;
 * this answers "what am I about to put there?". They share the panel and never
 * both show: arming a creation tool drops the selection, and placing something
 * selects it, which hands the panel back to the inspector.
 *
 * Everything here is decided ahead of the gesture on purpose. A wall drawn at
 * the wrong thickness, or an equipment dropped as an untyped box into the wrong
 * room, costs a hunt through the drawing to repair — while the same choice made
 * a second earlier costs nothing.
 */
import { UncalibratedBadge } from './UncalibratedBadge'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './../ui/button'
import { Input } from './../ui/input'
import { Label } from './../ui/label'
import { PlanEquipmentTypePicker, type PickedEquipmentType } from './EquipmentTypePicker'
import { EquipmentSizeChips } from './EquipmentSizeChips'
import { WallThicknessChips } from './WallThicknessChips'
import type { EquipmentSize } from './useEquipmentSizes'
import type { DrawParent } from './EditorToolbar'
import type { ToolId } from './engine/tools'
import type { NetworkLevel, PlanNetwork, RiserPassage } from './engine/scene'
import type { LevelParents } from './engine/network-assets'
import { NetworkSelect, RiserPassageSelect, RunLevelSwitch, RunWidthField } from './NetworkControls'

/** Walls thinner than this are a mis-typed value, not a design. */
const MIN_THICKNESS = 10

export interface ToolOptionsPanelProps {
    tool: ToolId
    org: string
    /** The thickness the next wall takes, and the plan's last five values. */
    thickness: number
    recentThicknesses: number[]
    onThicknessChange: (mm: number) => void
    /** The type the next equipment is placed as — null until one is picked. */
    equipmentType: PickedEquipmentType | null
    onEquipmentTypeChange: (picked: PickedEquipmentType) => void
    pickerOpen: boolean
    onPickerOpenChange: (open: boolean) => void
    /** The footprint the next one is placed with, and this type's recent ones. */
    equipmentSize: EquipmentSize
    recentSizes: EquipmentSize[]
    onEquipmentSizeChange: (size: EquipmentSize) => void
    /** The frame has no scale: the footprint is nominal, and this opens the calibrate flow. */
    uncalibrated?: boolean
    onCalibrate?: () => void
    /** Candidate parents inside this frame, and the pick — null is automatic. */
    parents: DrawParent[]
    parentUid: string | null
    onParentChange: (uid: string | null) => void
    /** The run tool: the catalogue, the armed network and the optional width. */
    networks?: PlanNetwork[]
    runNetwork?: string | null
    onRunNetworkChange?: (code: string) => void
    runWidthMm?: number | null
    /** The level the run tool files its network asset at, and the parents each level names. */
    runLevel?: NetworkLevel
    runLevelParents?: LevelParents
    onRunLevelChange?: (level: NetworkLevel) => void
    onRunWidthChange?: (mm: number | null) => void
    networkLabelOf?: (code: string) => string
    /** The riser tool: the armed network, diameter and passage. */
    riserNetwork?: string | null
    onRiserNetworkChange?: (code: string) => void
    riserDiameterMm?: number | null
    onRiserDiameterChange?: (mm: number | null) => void
    riserPassage?: RiserPassage
    onRiserPassageChange?: (passage: RiserPassage | undefined) => void
}

/** Millimetre field: Enter commits, Escape reverts, blur commits. */
function ThicknessField(props: { value: number; onCommit: (mm: number) => void }) {
    const { t } = useTranslation('assets')
    const [draft, setDraft] = useState(String(props.value))
    // Enter commits and then blurs, and the blur commits again in the same tick
    // — see the inspector's field for the same guard and why it matters.
    const committed = useRef(props.value)

    useEffect(() => {
        committed.current = props.value
        setDraft(String(props.value))
    }, [props.value])

    const commit = () => {
        const parsed = Number(draft)
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setDraft(String(committed.current))
            return
        }
        const clamped = Math.max(MIN_THICKNESS, Math.round(parsed))
        setDraft(String(clamped))
        if (clamped !== committed.current) {
            committed.current = clamped
            props.onCommit(clamped)
        }
    }

    return (
        <div className="flex items-center gap-2 py-1">
            <Label htmlFor="plan-editor-wall-thickness" className="w-28 shrink-0 text-xs text-muted-foreground">
                {t('planEditor.field_thickness')}
            </Label>
            <Input
                id="plan-editor-wall-thickness"
                data-testid="plan-editor-wall-thickness"
                type="number"
                className="h-7 text-xs"
                step={10}
                min={MIN_THICKNESS}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault()
                        commit()
                        ;(e.target as HTMLInputElement).blur()
                    } else if (e.key === 'Escape') {
                        e.preventDefault()
                        setDraft(String(props.value))
                        ;(e.target as HTMLInputElement).blur()
                    }
                }}
            />
        </div>
    )
}

export function ToolOptionsPanel(props: ToolOptionsPanelProps) {
    const { t } = useTranslation('assets')
    const { tool } = props
    // Walls, runs and risers join a bundle rather than becoming an asset each, and a
    // hole is not a thing at all: it is cut out of the room it lies in.
    const placesAsset = tool !== 'wall' && tool !== 'run' && tool !== 'riser' && tool !== 'hole'

    return (
        <div className="flex flex-col gap-1 p-3" data-testid="plan-editor-tool-options">
            <h2 className="text-sm font-semibold text-foreground">
                {t('planEditor.tool_options_title', { tool: t(`planEditor.tools.${tool}`) })}
            </h2>
            <p className="pb-1 text-[11px] text-muted-foreground" data-testid="plan-editor-tool-options-hint">
                {t(`planEditor.tool_options_hints.${tool}`)}
            </p>

            {tool === 'wall' && (
                <div className="rounded-md border border-border p-2" data-testid="plan-editor-wall-options">
                    <ThicknessField value={props.thickness} onCommit={props.onThicknessChange} />
                    <div className="pt-1">
                        <WallThicknessChips
                            recent={props.recentThicknesses}
                            active={props.thickness}
                            onPick={props.onThicknessChange}
                        />
                    </div>
                </div>
            )}

            {tool === 'equipment' && (
                <div className="rounded-md border border-border p-2" data-testid="plan-editor-equipment-options">
                    <div className="flex items-center gap-2 py-1">
                        <span className="w-28 shrink-0 text-xs text-muted-foreground">{t('planEditor.field_type')}</span>
                        <PlanEquipmentTypePicker
                            org={props.org}
                            open={props.pickerOpen}
                            onOpenChange={props.onPickerOpenChange}
                            onPick={(picked) => {
                                props.onEquipmentTypeChange(picked)
                                props.onPickerOpenChange(false)
                            }}
                        >
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 flex-1 justify-start text-xs"
                                data-testid="plan-editor-armed-type"
                                title={props.equipmentType?.code}
                            >
                                {props.equipmentType?.label ?? t('planEditor.choose_type')}
                            </Button>
                        </PlanEquipmentTypePicker>
                    </div>
                    {props.equipmentType && (
                        <p className="text-[11px] text-muted-foreground" data-testid="plan-editor-armed-footprint">
                            {t(props.uncalibrated ? 'planEditor.armed_footprint_nominal' : 'planEditor.armed_footprint', {
                                width: props.equipmentSize.width,
                                depth: props.equipmentSize.depth,
                            })}
                            {props.uncalibrated && (
                                <>
                                    {' '}
                                    <UncalibratedBadge />
                                    {props.onCalibrate && (
                                        <>
                                            {' '}
                                            <button
                                                type="button"
                                                className="underline"
                                                data-testid="plan-editor-armed-calibrate"
                                                onClick={props.onCalibrate}
                                            >
                                                {t('planEditor.calibrate')}
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </p>
                    )}
                    {props.equipmentType && (
                        <EquipmentSizeChips
                            recent={props.recentSizes}
                            active={props.equipmentSize}
                            onPick={props.onEquipmentSizeChange}
                        />
                    )}
                </div>
            )}

            {tool === 'run' && (
                <div className="rounded-md border border-border p-2" data-testid="plan-editor-run-options">
                    <div className="flex items-center gap-2 py-1">
                        <Label htmlFor="plan-editor-run-network" className="w-28 shrink-0 text-xs text-muted-foreground">
                            {t('planEditor.field_network')}
                        </Label>
                        <NetworkSelect
                            id="plan-editor-run-network"
                            testId="plan-editor-run-network"
                            networks={props.networks ?? []}
                            value={props.runNetwork ?? ''}
                            onChange={(code) => props.onRunNetworkChange?.(code)}
                            labelOf={props.networkLabelOf ?? ((c) => c)}
                        />
                    </div>
                    <div className="flex items-center gap-2 py-1">
                        <Label htmlFor="plan-editor-run-width" className="w-28 shrink-0 text-xs text-muted-foreground">
                            {t('planEditor.field_run_width')}
                        </Label>
                        <RunWidthField
                            id="plan-editor-run-width"
                            testId="plan-editor-run-width"
                            value={props.runWidthMm ?? null}
                            onCommit={(mm) => props.onRunWidthChange?.(mm)}
                        />
                    </div>
                    <div className="flex items-center gap-2 py-1">
                        <span className="w-28 shrink-0 text-xs text-muted-foreground">
                            {t('planEditor.field_run_level')}
                        </span>
                        <RunLevelSwitch
                            testId="plan-editor-run-level"
                            value={props.runLevel}
                            parents={props.runLevelParents ?? {}}
                            onChange={(level) => props.onRunLevelChange?.(level)}
                        />
                    </div>
                    {(props.networks ?? []).length === 0 && (
                        <p className="text-[11px] text-muted-foreground" data-testid="plan-editor-run-no-catalogue">
                            {t('planEditor.run_no_catalogue')}
                        </p>
                    )}
                </div>
            )}

            {tool === 'riser' && (
                <div className="rounded-md border border-border p-2" data-testid="plan-editor-riser-options">
                    <div className="flex items-center gap-2 py-1">
                        <Label htmlFor="plan-editor-riser-network" className="w-28 shrink-0 text-xs text-muted-foreground">
                            {t('planEditor.field_network')}
                        </Label>
                        <NetworkSelect
                            id="plan-editor-riser-network"
                            testId="plan-editor-riser-network"
                            networks={props.networks ?? []}
                            value={props.riserNetwork ?? ''}
                            onChange={(code) => props.onRiserNetworkChange?.(code)}
                            labelOf={props.networkLabelOf ?? ((c) => c)}
                        />
                    </div>
                    <div className="flex items-center gap-2 py-1">
                        <Label htmlFor="plan-editor-riser-diameter" className="w-28 shrink-0 text-xs text-muted-foreground">
                            {t('planEditor.field_riser_diameter')}
                        </Label>
                        <RunWidthField
                            id="plan-editor-riser-diameter"
                            testId="plan-editor-riser-diameter"
                            value={props.riserDiameterMm ?? null}
                            onCommit={(mm) => props.onRiserDiameterChange?.(mm)}
                        />
                    </div>
                    <div className="flex items-center gap-2 py-1">
                        <Label htmlFor="plan-editor-riser-passage" className="w-28 shrink-0 text-xs text-muted-foreground">
                            {t('planEditor.field_riser_passage')}
                        </Label>
                        <RiserPassageSelect
                            id="plan-editor-riser-passage"
                            testId="plan-editor-riser-passage"
                            value={props.riserPassage}
                            onChange={(p) => props.onRiserPassageChange?.(p)}
                        />
                    </div>
                    <p className="text-[11px] text-muted-foreground">{t('planEditor.riser_tool_hint')}</p>
                </div>
            )}

            {/*
              * The parent, for everything that creates an asset. Walls join a
              * bundle rather than becoming one, so their target is the asset
              * that owns the bundle — the same select, a different sentence.
              * A hole has neither: it belongs to the room it is cut out of.
              */}
            {tool !== 'hole' && (
            <div className="rounded-md border border-border p-2">
                <Label htmlFor="plan-editor-draw-parent" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t(placesAsset ? 'planEditor.parent_asset' : 'planEditor.wall_owner')}
                </Label>
                <select
                    id="plan-editor-draw-parent"
                    data-testid="plan-editor-draw-parent"
                    className="mt-1 h-7 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground"
                    value={props.parentUid ?? ''}
                    onChange={(e) => props.onParentChange(e.target.value || null)}
                >
                    <option value="">{t('planEditor.parent_automatic')}</option>
                    {props.parents.map((parent) => (
                        <option key={parent.uid} value={parent.uid}>{parent.name}</option>
                    ))}
                </select>
                <p className="pt-1 text-[11px] text-muted-foreground" data-testid="plan-editor-parent-auto-hint">
                    {t(`planEditor.parent_automatic_hints.${tool}`)}
                </p>
            </div>
            )}
        </div>
    )
}
